(() => {
'use strict';
const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const strings = window.themeStrings || {};
const t = (key, values = {}) => (strings[key] || key).replace(/\{\{\s*(\w+)\s*\}\}|__(\w+)__/g, (_, a, b) => values[a || b] ?? '');
const routes = window.themeRoutes || {cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update'};

/* ---------- Dialogs ---------- */
let returnFocus = null;
function updateSticky() {
  const dialogOpen = !!$('dialog[open]');
  $$('.sticky-buy').forEach(bar => { bar.hidden = bar._mainBuyVisible !== false || dialogOpen; });
}
function openDialog(dialog, trigger = document.activeElement) {
  if (!dialog) return;
  const active = $('dialog[open]');
  if (active && active !== dialog) active.close();
  else if (!active) returnFocus = trigger;
  if (!dialog.open) dialog.showModal();
  document.body.classList.add('modal-open');
  updateSticky();
}
function bindDialog(dialog) {
  if (dialog.dataset.bound) return;
  dialog.dataset.bound = '1';
  dialog.addEventListener('click', event => {
    if (event.target.closest('.close-dialog')) { dialog.close(); return; }
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    if ($('dialog[open]')) return;
    document.body.classList.remove('modal-open');
    if (returnFocus && returnFocus.isConnected) returnFocus.focus({preventScroll: true});
    updateSticky();
  });
}

/* ---------- Cart (Shopify AJAX API + section rendering) ---------- */
const cart = {
  dialog: () => $('#cart-dialog'),
  sectionIds() {
    const ids = ['cart-drawer'];
    const header = $('.site-header')?.closest('.shopify-section');
    if (header) ids.push(header.id.replace('shopify-section-', ''));
    return ids;
  },
  busy(state) { const inner = $('#cart-dialog .drawer-inner'); if (inner) inner.classList.toggle('cart-busy', state); },
  render(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (id === 'cart-drawer') {
        const fresh = $('.drawer-inner', doc);
        const current = $('#cart-dialog .drawer-inner');
        if (fresh && current) current.replaceWith(fresh);
      } else {
        const freshButton = $('.cart-open', doc);
        const currentButton = $('.cart-open');
        if (freshButton && currentButton) {
          currentButton.setAttribute('aria-label', freshButton.getAttribute('aria-label'));
          const freshCount = $('.cart-count', freshButton);
          const currentCount = $('.cart-count', currentButton);
          if (freshCount && currentCount) currentCount.replaceWith(freshCount);
        }
      }
    });
    const cartTotal = $('[data-cart-count]');
    if (cartTotal) cartTotal.dispatchEvent(new CustomEvent('cart:updated', {bubbles: true}));
  },
  async post(url, body) {
    const response = await fetch(url, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)});
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || t('cart_error'));
    return data;
  },
  async add(form) {
    const body = new FormData(form);
    body.append('sections', this.sectionIds().join(','));
    body.append('sections_url', location.pathname);
    const response = await fetch(`${routes.cartAdd}.js`, {method: 'POST', headers: {Accept: 'application/json'}, body});
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || t('cart_error'));
    this.render(data.sections);
    return data;
  },
  async change(key, quantity) {
    this.busy(true);
    try {
      const data = await this.post(`${routes.cartChange}.js`, {id: key, quantity, sections: this.sectionIds().join(','), sections_url: location.pathname});
      this.render(data.sections);
      if (location.pathname.replace(/\/$/, '').endsWith(routes.cart)) location.reload();
    } catch (error) {
      const message = $('#cart-dialog .form-message');
      if (message) { message.hidden = false; message.textContent = error.message; }
    } finally { this.busy(false); }
  },
  async note(value) {
    try { await this.post(`${routes.cartUpdate}.js`, {note: value}); } catch { /* keep typing */ }
  }
};
let noteTimer;
document.addEventListener('click', event => {
  const open = event.target.closest('.cart-open');
  if (open) { event.preventDefault(); openDialog(cart.dialog(), open); return; }
  const quantity = event.target.closest('[data-cart-quantity]');
  if (quantity) { cart.change(quantity.dataset.key, Number(quantity.dataset.cartQuantity)); return; }
  const remove = event.target.closest('[data-cart-remove]');
  if (remove) { event.preventDefault(); cart.change(remove.dataset.key, 0); }
});
document.addEventListener('input', event => {
  if (!event.target.matches('[data-cart-note]')) return;
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => cart.note(event.target.value), 500);
});

/* ---------- Product purchase block ---------- */
function enableMouseDragging(scroller, state, goTo) {
  let drag = null;
  scroller.addEventListener('pointerdown', event => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    drag = {x: event.clientX, left: scroller.scrollLeft, index: state.index, id: event.pointerId};
    scroller.setPointerCapture(event.pointerId);
    scroller.classList.add('dragging');
  });
  scroller.addEventListener('pointermove', event => { if (drag) scroller.scrollLeft = drag.left - (event.clientX - drag.x); });
  scroller.addEventListener('pointerup', event => {
    if (!drag) return;
    const movement = event.clientX - drag.x;
    const next = Math.abs(movement) > 40 ? drag.index + (movement < 0 ? 1 : -1) : drag.index;
    const pointerId = drag.id;
    drag = null;
    scroller.classList.remove('dragging');
    if (scroller.hasPointerCapture(pointerId)) scroller.releasePointerCapture(pointerId);
    goTo(next);
  });
  const cancel = () => { if (!drag) return; const index = drag.index; drag = null; scroller.classList.remove('dragging'); goTo(index, false); };
  scroller.addEventListener('pointercancel', cancel);
  scroller.addEventListener('lostpointercapture', cancel);
}

function initGallery(root) {
  const gallery = $('.gallery', root);
  if (!gallery) return null;
  const zoomDialog = $('.zoom-dialog', root);
  const zoomGallery = $('.zoom-gallery', root);
  const slides = $$('.slide', gallery);
  const total = slides.length;
  const names = slides.map((slide, i) => slide.dataset.name || t('photo_n', {number: i + 1}));
  const state = {index: 0};
  if (zoomGallery && !zoomGallery.children.length) slides.forEach(slide => zoomGallery.append(slide.cloneNode(true)));
  const thumbs = $$('.thumbnail', root);
  const count = $('.gallery-count', root);
  const status = $('.gallery-status', root);
  const prev = $('.gallery-prev', root), next = $('.gallery-next', root);
  const zoomPrev = $('.zoom-prev', root), zoomNext = $('.zoom-next', root), zoomCount = $('.zoom-count', root), zoomTitle = $('.zoom-title', root);

  function updateZoom() {
    if (!zoomDialog) return;
    if (zoomTitle) zoomTitle.textContent = names[state.index];
    if (zoomCount) zoomCount.textContent = `${state.index + 1} / ${total}`;
    if (zoomPrev) zoomPrev.disabled = state.index === 0;
    if (zoomNext) zoomNext.disabled = state.index === total - 1;
  }
  function update(index) {
    state.index = Math.max(0, Math.min(total - 1, index));
    thumbs.forEach((thumb, i) => i === state.index ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current'));
    if (count) count.textContent = `${state.index + 1} / ${total}`;
    if (status) status.textContent = t('photo_status', {number: state.index + 1, total, name: names[state.index]});
    if (prev) prev.disabled = state.index === 0;
    if (next) next.disabled = state.index === total - 1;
    if (zoomDialog?.open) updateZoom();
  }
  function goTo(index, smooth = true) {
    index = Math.max(0, Math.min(total - 1, index));
    const zoomOpen = !!zoomDialog?.open;
    gallery.scrollTo({left: index * gallery.clientWidth, behavior: smooth && !zoomOpen && !reducedMotion.matches ? 'smooth' : 'instant'});
    if (zoomOpen) zoomGallery.scrollTo({left: index * zoomGallery.clientWidth, behavior: 'instant'});
    update(index);
  }
  let tick = false;
  gallery.addEventListener('scroll', () => {
    if (!tick) requestAnimationFrame(() => { update(Math.round(gallery.scrollLeft / gallery.clientWidth)); tick = false; });
    tick = true;
  }, {passive: true});
  thumbs.forEach(button => button.addEventListener('click', () => goTo(Number(button.dataset.slide))));
  prev?.addEventListener('click', () => goTo(state.index - 1));
  next?.addEventListener('click', () => goTo(state.index + 1));
  const keys = event => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); goTo(state.index + (event.key === 'ArrowRight' ? 1 : -1)); }
    else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); goTo(event.key === 'Home' ? 0 : total - 1); }
  };
  gallery.addEventListener('keydown', keys);
  enableMouseDragging(gallery, state, goTo);
  let width = gallery.clientWidth;
  new ResizeObserver(() => { if (width !== gallery.clientWidth) { width = gallery.clientWidth; goTo(state.index, false); } }).observe(gallery);

  if (zoomDialog && zoomGallery) {
    bindDialog(zoomDialog);
    $('.zoom-open', root)?.addEventListener('click', event => {
      openDialog(zoomDialog, event.currentTarget);
      zoomGallery.scrollTo({left: state.index * zoomGallery.clientWidth, behavior: 'instant'});
      updateZoom();
    });
    zoomPrev?.addEventListener('click', () => goTo(state.index - 1, false));
    zoomNext?.addEventListener('click', () => goTo(state.index + 1, false));
    zoomDialog.addEventListener('keydown', keys);
    enableMouseDragging(zoomGallery, state, goTo);
    let zoomTick = false;
    zoomGallery.addEventListener('scroll', () => {
      if (!zoomTick) requestAnimationFrame(() => {
        if (zoomDialog.open && zoomGallery.clientWidth) {
          const index = Math.round(zoomGallery.scrollLeft / zoomGallery.clientWidth);
          if (index !== state.index) { gallery.scrollTo({left: index * gallery.clientWidth, behavior: 'instant'}); update(index); }
        }
        zoomTick = false;
      });
      zoomTick = true;
    }, {passive: true});
    let zoomWidth = 0;
    new ResizeObserver(() => {
      const w = zoomGallery.clientWidth;
      if (zoomDialog.open && w && w !== zoomWidth) zoomGallery.scrollTo({left: state.index * w, behavior: 'instant'});
      zoomWidth = w;
    }).observe(zoomGallery);
  }
  update(0);
  return {goTo, state};
}

function initProductBuy(root) {
  if (root.dataset.init) return;
  root.dataset.init = '1';
  const gallery = initGallery(root);
  const form = $('form.buy-form', root);
  const sticky = $('.sticky-buy', root);
  const mainBuy = $('.main-buy', root);
  const message = $('.form-message', root);
  $$('dialog', root).forEach(bindDialog);

  if (sticky && mainBuy) {
    sticky._mainBuyVisible = true;
    new IntersectionObserver(([entry]) => {
      sticky._mainBuyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.99;
      updateSticky();
    }, {threshold: [0, 1], rootMargin: '0px 0px -84px 0px'}).observe(mainBuy);
    $('.sticky-buy .primary', root)?.addEventListener('click', () => form?.requestSubmit());
  }

  const payOpen = $('.pay-open', root);
  const payDialog = $('.pay-dialog', root);
  if (payOpen && payDialog) payOpen.addEventListener('click', () => openDialog(payDialog, payOpen));

  if (form) {
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const button = form.querySelector('[type=submit]');
      if (button?.disabled) return;
      if (message) message.hidden = true;
      button?.classList.add('loading');
      try {
        await cart.add(form);
        openDialog(cart.dialog(), event.submitter || mainBuy);
      } catch (error) {
        if (message) { message.hidden = false; message.textContent = error.message; }
      } finally { button?.classList.remove('loading'); }
    });

    const variants = JSON.parse($('[data-variants]', root)?.textContent || '[]');
    root.addEventListener('change', async event => {
      if (!event.target.matches('.option-values input')) return;
      const selected = $$('fieldset', root).map(group => $('input:checked', group)?.value);
      const variant = variants.find(v => v.options.every((value, i) => value === selected[i]));
      const idInput = $('input[name=id]', form);
      if (!variant) {
        mainBuy.disabled = true;
        mainBuy.textContent = t('unavailable');
        return;
      }
      idInput.value = variant.id;
      if (root.dataset.updateUrl === 'true') history.replaceState(null, '', `${location.pathname}?variant=${variant.id}`);
      if (variant.featured_media && gallery) {
        const position = $$('.slide', root).findIndex(slide => Number(slide.dataset.mediaId) === variant.featured_media.id);
        if (position >= 0) gallery.goTo(position);
      }
      try {
        const url = `${root.dataset.url}?variant=${variant.id}&section_id=${root.dataset.section}`;
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['.price', '.sticky-price'].forEach(selector => {
          const fresh = $(selector, doc), current = $(selector, root);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        const freshBuy = $('.main-buy', doc);
        if (freshBuy && mainBuy) { mainBuy.disabled = freshBuy.disabled; mainBuy.innerHTML = freshBuy.innerHTML; }
        const freshSticky = $('.sticky-buy .primary', doc), currentSticky = $('.sticky-buy .primary', root);
        if (freshSticky && currentSticky) { currentSticky.disabled = freshSticky.disabled; currentSticky.textContent = freshSticky.textContent; }
      } catch { /* price stays until next change */ }
    });
  }
}

/* ---------- Accordions ---------- */
function initAccordions(root = document) {
  $$('.details-list details', root).forEach(details => {
    if (details.dataset.animated) return;
    details.dataset.animated = '1';
    const summary = $('summary', details);
    const content = $('.detail-content', details);
    if (!summary || !content) return;
    let running = null;
    summary.addEventListener('click', event => {
      event.preventDefault();
      if (reducedMotion.matches) { details.open = !details.open; return; }
      if (running) running.cancel();
      const duration = 280, easing = 'cubic-bezier(.2,.7,.2,1)';
      content.style.overflow = 'hidden';
      if (!details.open) {
        details.open = true;
        const height = content.scrollHeight;
        running = content.animate([{height: '0px', opacity: 0}, {height: `${height}px`, opacity: 1}], {duration, easing});
        running.onfinish = running.oncancel = () => { content.style.overflow = ''; running = null; };
      } else {
        const height = content.offsetHeight;
        running = content.animate([{height: `${height}px`, opacity: 1}, {height: '0px', opacity: 0}], {duration: duration - 60, easing});
        running.onfinish = () => { details.open = false; content.style.overflow = ''; running = null; };
        running.oncancel = () => { content.style.overflow = ''; running = null; };
      }
    });
  });
}

function init() {
  $$('dialog').forEach(bindDialog);
  initAccordions();
  $$('[data-product-buy]').forEach(initProductBuy);
}
init();
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:unload', () => { document.body.classList.remove('modal-open'); });
})();
